import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'utils/i18n';
import { useDarkMode } from 'utils/darkMode';
import { LEGAL_PAGES, getCurrentLegalPage } from './legalPagesConfig';
import { verticalTabs, colors, typography, spacing } from './legalStyles';
import sharedTranslations from '../shared/translations';

/**
 * LegalNavigation - Vertical tab navigation for legal pages
 * Displays as a vertical list in the left sidebar (desktop) or dropdown (mobile)
 * Props:
 * - mobileOnly: if true, only show mobile dropdown; if false, only show desktop navigation
 */
const LegalNavigation = ({ mobileOnly = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { isDarkMode } = useDarkMode();

  // Get current legal page
  const currentPage = useMemo(() => {
    return getCurrentLegalPage(location.pathname);
  }, [location.pathname]);

  // Get language prefix from current path
  const langPrefix = useMemo(() => {
    const match = location.pathname.match(/^\/([a-z]{2})\//);
    return match ? `/${match[1]}` : '';
  }, [location.pathname]);

  // Navigate to legal page
  const handleTabClick = (page) => {
    const targetPath = langPrefix ? `${langPrefix}${page.path}` : page.path;
    navigate(targetPath);
  };

  // Check if a page is active
  const isActive = (page) => {
    return currentPage && currentPage.id === page.id;
  };

  // Get translated label
  const getLabel = (page) => {
    // Try to get translation, fallback to label
    const translated = t('title', page.translationKey);
    return translated && translated !== page.label ? translated : page.label;
  };

  const containerStyle = {
    ...verticalTabs.container
  };

  const getTabStyle = (active) => ({
    ...verticalTabs.tab,
    ...(active ? verticalTabs.tabActive : {}),
    color: active 
      ? (isDarkMode ? '#60a5fa' : colors.primary)
      : (isDarkMode ? '#cbd5e0' : colors.textSecondary),
    backgroundColor: active && isDarkMode ? 'rgba(96, 165, 250, 0.1)' : (active ? colors.primaryLight : 'transparent')
  });

  // Get translation for "Relevant Content"
  const translations = sharedTranslations[language] || sharedTranslations.en;
  const relevantContentLabel = translations?.legal?.legalNavigation?.relevantContent || 'Relevant Content';

  // Header style for "Relevant Content" - compact and aligned
  const headerStyle = {
    ...typography.h3,
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: spacing.sm,
    marginTop: 0,
    color: isDarkMode ? '#e5e7eb' : colors.text,
    padding: `0 ${spacing.md}`,
    letterSpacing: '-0.01em',
    lineHeight: '1.3'
  };

  // Mobile dropdown handler
  const handleMobileSelect = (e) => {
    const selectedPage = LEGAL_PAGES.find(p => p.id === e.target.value);
    if (selectedPage) {
      handleTabClick(selectedPage);
    }
  };

  // If mobileOnly is explicitly set, show only that version
  if (mobileOnly === true) {
    return (
      <div className="legal-navigation-mobile">
        <select
          value={currentPage?.id || ''}
          onChange={handleMobileSelect}
          aria-label="Select legal document"
          className="legal-nav-dropdown"
        >
          {LEGAL_PAGES.map((page) => (
            <option key={page.id} value={page.id}>
              {getLabel(page)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (mobileOnly === false) {
    return (
      <nav 
        className="legal-navigation-vertical legal-navigation-desktop" 
        aria-label="Legal documents navigation"
        style={containerStyle}
      >
        <h3 style={headerStyle}>{relevantContentLabel}</h3>
        {LEGAL_PAGES.map((page) => {
          const active = isActive(page);
          return (
            <button
              key={page.id}
              onClick={() => handleTabClick(page)}
              aria-current={active ? 'page' : undefined}
              className={`legal-nav-tab-vertical ${active ? 'active' : ''}`}
              style={getTabStyle(active)}
              onMouseEnter={(e) => {
                if (!active) {
                  e.target.style.backgroundColor = isDarkMode ? '#2d3748' : colors.backgroundHover;
                  e.target.style.color = isDarkMode ? '#e2e8f0' : colors.text;
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = isDarkMode ? '#cbd5e0' : colors.textSecondary;
                }
              }}
            >
              {getLabel(page)}
            </button>
          );
        })}
      </nav>
    );
  }

  // Default: Show both, CSS will handle visibility
  return (
    <>
      {/* Desktop: Vertical tab navigation */}
      <nav 
        className="legal-navigation-vertical legal-navigation-desktop" 
        aria-label="Legal documents navigation"
        style={containerStyle}
      >
        <h3 style={headerStyle}>{relevantContentLabel}</h3>
        {LEGAL_PAGES.map((page) => {
          const active = isActive(page);
          return (
            <button
              key={page.id}
              onClick={() => handleTabClick(page)}
              aria-current={active ? 'page' : undefined}
              className={`legal-nav-tab-vertical ${active ? 'active' : ''}`}
              style={getTabStyle(active)}
              onMouseEnter={(e) => {
                if (!active) {
                  e.target.style.backgroundColor = isDarkMode ? '#2d3748' : colors.backgroundHover;
                  e.target.style.color = isDarkMode ? '#e2e8f0' : colors.text;
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = isDarkMode ? '#cbd5e0' : colors.textSecondary;
                }
              }}
            >
              {getLabel(page)}
            </button>
          );
        })}
      </nav>

      {/* Mobile: Dropdown select */}
      <div className="legal-navigation-mobile">
        <select
          value={currentPage?.id || ''}
          onChange={handleMobileSelect}
          aria-label="Select legal document"
          className="legal-nav-dropdown"
        >
          {LEGAL_PAGES.map((page) => (
            <option key={page.id} value={page.id}>
              {getLabel(page)}
            </option>
          ))}
        </select>
      </div>
    </>
  );
};

export default LegalNavigation;
